import React, { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Button,
  MenuItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectFieldTrigger,
  ToggleGroup,
  ToggleItem,
} from '@crabs-ai/ds-react';

import {
  CAPTION_TARGET_LANGUAGES,
  captionTargetLanguageLabelKey,
  DEFAULT_CAPTION_TARGET_LANGUAGE,
  type CaptionTargetLanguage,
} from '@/shared/lib/captions/caption-translation-languages';
import { usePostCaptionTranslationApi } from '@/shared/apis/editor/post-caption-translation';

import { useProjectTypeContext } from '../../../contexts/project-type-context';
import { isCaptionAsset } from '../../../assets/assets';
import {
  applyCaptionTranslations,
  applyTitleTranslations,
  buildCaptionTranslationSlots,
  buildTitleTranslationRequests,
  hasStoredTitleTranslation,
  setCaptionDisplayLanguage,
  titleDisplayLanguageOf,
  type CaptionDisplayLanguage,
} from '../../../state/actions/caption-translation-actions';
import { isTextItem } from '../../../items/text/text-item-type';
import {
  useCurrentStateAsRef,
  useFullState,
  useWriteContext,
} from '../../../utils/use-context';
import { InspectorSubLabel } from '../../components/inspector-label';

/**
 * LTS 자막 번역 컨트롤 — 에디터에서 자막을 타겟 언어로 번역하고 원문↔번역을 전환한다.
 *
 * [번역]은 아직 그 언어로 번역되지 않은 캡션만 서버에 보낸다(생성 시 번역을 안 켠 프로젝트,
 * 자막 확장으로 새로 생긴 조각 포함). 전환은 서버를 부르지 않고 asset 안의 두 벌만 스왑한다.
 */
export const CaptionTranslationControls: React.FC = () => {
  const t = useTranslations();
  const state = useFullState();
  const stateAsRef = useCurrentStateAsRef();
  const { setState } = useWriteContext();
  const { projectMetadata } = useProjectTypeContext();
  const { mutateAsync, isPending } = usePostCaptionTranslationApi();

  const [targetLanguage, setTargetLanguage] = useState<CaptionTargetLanguage>(
    DEFAULT_CAPTION_TARGET_LANGUAGE,
  );
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { hasTranslation, displayLanguage } = useMemo(() => {
    const captionAssets = Object.values(state.undoableState.assets).filter(
      (asset) => isCaptionAsset(asset),
    );
    const translatedCaptions = captionAssets.filter(
      (asset) => (asset.translatedCaptions?.length ?? 0) > 0,
    );
    const translatedTitles = Object.values(state.undoableState.items).filter(
      hasStoredTitleTranslation,
    );

    return {
      hasTranslation:
        translatedCaptions.length > 0 || translatedTitles.length > 0,
      displayLanguage:
        translatedCaptions.some(
          (asset) =>
            asset.captionLanguage !== undefined &&
            asset.captionLanguage !== 'source',
        ) ||
        translatedTitles.some(
          (item) =>
            isTextItem(item) && titleDisplayLanguageOf(item) !== 'source',
        )
          ? ('translated' as const)
          : ('source' as const),
    };
  }, [state.undoableState.assets, state.undoableState.items]);

  const { untranslatedCount, hasUntranslatedTitle } = useMemo(() => {
    const slots = buildCaptionTranslationSlots(
      state.undoableState.assets,
      targetLanguage,
    );
    const titles = buildTitleTranslationRequests(
      state.undoableState.items,
      targetLanguage,
    );
    return {
      untranslatedCount: slots.length,
      hasUntranslatedTitle: titles.length > 0,
    };
  }, [state.undoableState.assets, state.undoableState.items, targetLanguage]);

  const handleSelectLanguage = useCallback(
    (language: CaptionTargetLanguage) => {
      setTargetLanguage(language);
      setIsLanguageOpen(false);
      setErrorMessage(null);
    },
    [],
  );

  const handleTranslate = useCallback(async () => {
    const projectId = projectMetadata.projectId;
    if (projectId == null) {
      setErrorMessage(
        t('Editor.inspector.caption-translation.errors.no-project'),
      );
      return;
    }

    const slots = buildCaptionTranslationSlots(
      stateAsRef.current.undoableState.assets,
      targetLanguage,
    );
    const titles = buildTitleTranslationRequests(
      stateAsRef.current.undoableState.items,
      targetLanguage,
    );
    if (slots.length === 0 && titles.length === 0) return;

    setErrorMessage(null);

    try {
      const response = await mutateAsync({
        projectId,
        targetLanguage,
        ...(projectMetadata.storyId != null
          ? { storyId: projectMetadata.storyId }
          : {}),
        slots,
        titles,
      });

      const results = response.data?.slots ?? [];
      const titleResults = response.data?.titles ?? [];
      const usableCount =
        results.filter((result) => result.translation?.status !== 'failed')
          .length + titleResults.length;

      if (usableCount === 0) {
        setErrorMessage(t('Editor.inspector.caption-translation.errors.empty'));
        return;
      }

      setState({
        update: (current) => {
          const withCaptions = applyCaptionTranslations(
            current,
            results,
            targetLanguage,
          );
          const withTitles = applyTitleTranslations(
            withCaptions,
            titleResults,
            targetLanguage,
          );
          return setCaptionDisplayLanguage(
            withTitles,
            'translated',
            targetLanguage,
          );
        },
        commitToUndoStack: true,
      });
    } catch {
      setErrorMessage(t('Editor.inspector.caption-translation.errors.request'));
    }
  }, [
    mutateAsync,
    projectMetadata.projectId,
    projectMetadata.storyId,
    setState,
    stateAsRef,
    t,
    targetLanguage,
  ]);

  const handleChangeDisplayLanguage = useCallback(
    (value: CaptionDisplayLanguage) => {
      setErrorMessage(null);
      setState({
        update: (current) =>
          setCaptionDisplayLanguage(current, value, targetLanguage),
        commitToUndoStack: true,
      });
    },
    [setState, targetLanguage],
  );

  const isTranslateDisabled =
    isPending || (untranslatedCount === 0 && !hasUntranslatedTitle);

  return (
    <div className="flex flex-col gap-4xs">
      <div className="flex flex-col gap-3xs">
        <InspectorSubLabel>
          {t('Editor.inspector.caption-translation.language-label')}
        </InspectorSubLabel>
        <Popover
          open={isLanguageOpen}
          onOpenChange={setIsLanguageOpen}
        >
          <PopoverTrigger asChild>
            <SelectFieldTrigger
              value={t(captionTargetLanguageLabelKey(targetLanguage))}
              disabled={isPending}
              accessibilityLabel={t(
                'Editor.inspector.caption-translation.language-label',
              )}
            />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 px-3xs py-2xs inset-shadow-basic-md"
          >
            {CAPTION_TARGET_LANGUAGES.map((language) => (
              <MenuItem
                key={language}
                size="md"
                text={t(captionTargetLanguageLabelKey(language))}
                singleSelectType
                className="w-full rounded-xs"
                isSelected={targetLanguage === language}
                onClick={() => handleSelectLanguage(language)}
              />
            ))}
          </PopoverContent>
        </Popover>
      </div>

      <p className="text-caption-small text-sys-cont-neutral-light">
        {t('Editor.inspector.caption-translation.description')}
      </p>

      {errorMessage && (
        <p
          role="alert"
          className="text-caption-small text-sys-cont-negative-default"
        >
          {errorMessage}
        </p>
      )}

      <Button
        type="button"
        size="small"
        hierarchy="tertiary"
        onClick={handleTranslate}
        state={
          isPending ? 'loading' : isTranslateDisabled ? 'disabled' : 'default'
        }
        disabled={isTranslateDisabled}
        className="w-full"
      >
        {isPending
          ? t('Editor.inspector.caption-translation.translating')
          : t('Editor.inspector.caption-translation.translate', {
              count: untranslatedCount,
            })}
      </Button>

      {hasTranslation && (
        <ToggleGroup
          type="single"
          value={displayLanguage}
          disabled={isPending}
          onValueChange={(value) => {
            if (value === 'source' || value === 'translated') {
              handleChangeDisplayLanguage(value);
            }
          }}
          className="flex flex-wrap"
        >
          <ToggleItem
            value="source"
            aria-label={t('Editor.inspector.caption-translation.show-source')}
            label={t('Editor.inspector.caption-translation.show-source')}
            showIcon={false}
          />
          <ToggleItem
            value="translated"
            aria-label={t(
              'Editor.inspector.caption-translation.show-translated',
            )}
            label={t('Editor.inspector.caption-translation.show-translated')}
            showIcon={false}
          />
        </ToggleGroup>
      )}
    </div>
  );
};
